import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

export default function GroupementSAR() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isAlreadyMember, setIsAlreadyMember] = useState(false);
  const [membershipStatus, setMembershipStatus] = useState(null);
  const [composition, setComposition] = useState([]);

  useEffect(() => {
    fetchComposition();
    if (user) {
      checkMembership();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchComposition = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/composition/SAR`
      );
      setComposition(response.data);
    } catch (error) {
      console.error('Erreur lors de la récupération de la composition:', error);
    }
  };

  const formatTitre = (titre) => titre.charAt(0).toUpperCase() + titre.slice(1);
  const formatFonction = (fonction) => fonction ? fonction.charAt(0).toUpperCase() + fonction.slice(1) : '';

  const checkMembership = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/adhesions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const organismesAvecStatut = response.data.organismesAvecStatut || [];
      const sarMembership = organismesAvecStatut.find(o => o.nom === 'SAR');
      
      if (sarMembership) {
        setIsAlreadyMember(true);
        setMembershipStatus(sarMembership.status);
      }
    } catch (error) {
      console.error('Erreur lors de la vérification de l\'adhésion:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdherer = () => {
    navigate('/adhesion/sar');
  };

  const getStatusLabel = (status) => {
    const labels = {
      'en_cours': 'En cours',
      'en_attente': 'En attente',
      'actif': 'Actif',
      'refuse': 'Refusé'
    };
    return labels[status] || status;
  };

  const getStatusColor = (status) => {
    const colors = {
      'en_cours': 'bg-yellow-100 text-yellow-800',
      'en_attente': 'bg-honey-100 text-honey-800',
      'actif': 'bg-green-100 text-green-800',
      'refuse': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-slate-100 text-slate-800';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-honey-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* En-tête */}
        <div className="flex items-center justify-start mb-12">
          <img src="/logos/logo_sar.png" alt="SAR" className="w-auto h-48 pr-12" />
          <div className="text-left">
            <h1 className="text-4xl font-extrabold text-slate-900 sm:text-5xl">
              SAR
            </h1>
            <p className="mt-3 text-xl text-slate-600">
              Le Syndicat Apicole de la Réunion
            </p>
          </div>
        </div>

        {/* Carte de description */}
        <div className="bg-white shadow-lg rounded-xl overflow-hidden mb-8">
          <div className="p-6 md:p-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">
              À propos du SAR
            </h2>
            <div className="prose prose-indigo max-w-none">
              <p className="text-slate-700 mb-4 text-justify">
                Le syndicat créé en 1996 a pour vocation de <span className="font-bold">défendre les intérêts des apiculteurs de La Réunion</span>.<br/>
                Le syndicat assure la promotion et la valorisation des métiers de l'apiculture, et des produits de la ruche.<br/>
                Le syndicat veille à la sauvegarde du cheptel apicole.<br/>
                Le syndicat participe à la <span className="font-bold">formation des apiculteurs (formateurs bénévoles)</span>.<br/>
                Le syndicat participe à la protection des ressources mellifères.<br/>
                Le syndicat fait connaître l'abeille et son rôle dans la pollinisation et le maintien de la biodiversité.<br/>
                Le syndicat est adhèrent de <span className="font-bold">l’Union nationale de l’apiculture française (UNAF)</span> qui est un syndicat apicole représentatif regroupant plus de 100 syndicats et organisations départementales.
              </p>
              <p className="text-slate-700 mb-4 text-justify">
                Avec le soutien de l'UNAF, le syndicat a créé et gère des ruchers locaux du programme Abeille Sentinelle de l'Environnement (ASE) qui a pour mission de sensibiliser, informer et impliquer le grand public sur l’importance des abeilles et des pollinisateurs dans notre écosystème
                Grace à l’UNAF, le Syndicat Apicole de La Réunion propose à ses adhérents <span className="font-bold">de nombreux services à des tarifs accessibles comme</span> :
              </p>
              <ul className="list-disc list-inside space-y-2 text-slate-700">
                <li className='flex items-start text-justify'>
                  <svg className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>
                    <span className="font-bold">l’abonnement à Abeilles & Fleurs</span>, la revue apicole mensuelle de référence, éditée par l’UNAF ;
                  </span>
                </li>
                <li className='flex items-start text-justify'>
                  <svg className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>
                    <span className="font-bold">la collecte de l’éco-contribution</span> permettant la conformité des apiculteurs avec la réglementation via la convention collective entre l’UNAF et Adelphe (filiale de Citéo) ;
                  </span>
                </li>
                <li className='flex items-start text-justify'>
                  <svg className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>
                    <span className="font-bold">la souscription à l’assurance des ruches</span>, via le contrat d’assurance ruches conclu entre GROUPAMA et l’UNAF pour les apiculteurs adhérents.
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Composition du conseil et du bureau */}
        {composition.length > 0 && (
          <div className="bg-white shadow-lg rounded-xl overflow-hidden mb-8">
            <div className="p-6 md:p-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">
                Composition du conseil et du bureau
              </h2>
              
              {/* Bureau */}
              {composition.filter(m => m.roles.some(r => r.titre === 'membre du bureau')).length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-indigo-900 mb-3 flex items-center">
                    <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    Membres du Bureau
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {composition
                      .filter(m => m.roles.some(r => r.titre === 'membre du bureau'))
                      .map((membre, idx) => (
                        <div key={idx} className="bg-indigo-50 rounded-lg p-3">
                          <p className="font-medium text-indigo-900">{membre.prenom} {membre.nom}</p>
                          {membre.roles
                            .filter(r => r.titre === 'membre du bureau')
                            .map((role, rIdx) => (
                              <p key={rIdx} className="text-sm text-indigo-600">
                                {role.fonction ? formatFonction(role.fonction) : 'Membre du bureau'}
                              </p>
                            ))}
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* Conseil syndical */}
              {composition.filter(m => m.roles.some(r => r.titre !== 'membre du bureau')).length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-honey-900 mb-3 flex items-center">
                    <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                    Membres du Conseil Syndical
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {composition
                      .filter(m => m.roles.some(r => r.titre !== 'membre du bureau'))
                      .map((membre, idx) => (
                        <div key={idx} className="bg-honey-50 rounded-lg p-3">
                          <p className="font-medium text-honey-900">{membre.prenom} {membre.nom}</p>
                          {membre.roles
                            .filter(r => r.titre !== 'membre du bureau')
                            .map((role, rIdx) => (
                              <p key={rIdx} className="text-sm text-honey-600">
                                {formatTitre(role.titre)}
                              </p>
                            ))}
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* External links */}
        {/* <div className="bg-white shadow-lg rounded-xl overflow-hidden mb-8">
          <div className="p-6 md:p-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">
              En savoir plus
            </h2>
            <div className="prose prose-indigo max-w-none">
              <Link className="text-slate-700 mb-4 underline">
                Visitez le site du SAR
              </Link>
              <p className="text-slate-700 mb-4 pt-2">
                referent@sar.com
              </p>
            </div>
          </div>
        </div> */}

        {/* Bouton d'adhésion */}
        <div className="bg-white shadow-lg rounded-lg overflow-hidden">
          <div className="px-6 py-8">
            {isAlreadyMember ? (
              <div className="text-center">
                <div className="mb-4">
                  <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold ${getStatusColor(membershipStatus)}`}>
                    Déjà adhérent - {getStatusLabel(membershipStatus)}
                  </span>
                </div>
                <p className="text-slate-600 mb-6">
                  Vous êtes déjà membre du SAR. Consultez votre espace personnel pour plus d'informations.
                </p>
                <button
                  onClick={() => navigate('/dashboard')}
                  className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-honey-500 hover:bg-honey-600"
                >
                  Voir mon espace personnel
                </button>
              </div>
            ) : (
              <div className="text-center">
                <h3 className="text-2xl font-bold text-slate-900 mb-4">
                  Rejoignez le SAR
                </h3>
                <p className="text-slate-600 mb-6">
                  Complétez votre demande d'adhésion en quelques minutes
                </p>
                <button
                  onClick={handleAdherer}
                  className="w-full items-center px-8 py-4 border border-transparent text-lg font-medium rounded-lg text-white bg-honey-500 hover:bg-honey-600 shadow-lg hover:shadow-xl transition-all"
                >
                  Adhérer au SAR
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Bouton retour */}
        <div className="mt-8 text-center">
          <button
            onClick={() => navigate('/')}
            className="text-honey-600 hover:text-honey-800 font-medium"
          >
            ← Retour à l'accueil
          </button>
        </div>
      </div>
    </div>
  );
}
